import React, { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  Sparkles,
  Loader2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
} from 'lucide-react'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import recruiterJobService from '@/services/recruiterJobService'
import RangeSlider from '@/components/common/RangeSlider'
import type { CreateMatchRequest, RecruiterJob } from '@/types'

interface JobDescriptionFormProps {
  onSubmit: (request: CreateMatchRequest) => Promise<void>
  onPreview?: (request: CreateMatchRequest) => Promise<void>
  isLoading?: boolean
  isLoadingPreview?: boolean
  embeddingsError?: { jobId: string } | null
  onReprocess?: (jobId: string) => Promise<void>
  isReprocessing?: boolean
}

export const JobDescriptionForm: React.FC<JobDescriptionFormProps> = ({
  onSubmit,
  isLoading = false,
  isLoadingPreview = false,
  embeddingsError = null,
  onReprocess,
  isReprocessing = false,
}) => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [jobs, setJobs] = useState<RecruiterJob[]>([])
  const [jobsLoading, setJobsLoading] = useState(true)
  const [selectedJobId, setSelectedJobId] = useState<string>('')
  const [formData, setFormData] = useState<CreateMatchRequest>({
    recruiterJobId: '',
    maxResults: 3,
    filters: {
      location: [],
      minExperience: undefined,
      maxExperience: undefined,
      employmentStatus: [],
      minProfileCompletion: 0,
    },
  })

  const [locationInput, setLocationInput] = useState('')

  const employmentStatusOptions = [
    { value: 'ACTIVELY_LOOKING', label: 'Actively Looking' },
    { value: 'OPEN_TO_OFFERS', label: 'Open to Offers' },
    { value: 'NOT_LOOKING', label: 'Not Looking' },
    { value: 'EMPLOYED', label: 'Employed' },
    { value: 'UNEMPLOYED', label: 'Unemployed' },
  ]

  useEffect(() => {
    const preselectedJobId = searchParams.get('jobId')
    recruiterJobService
      .listJobs({ limit: 100 })
      .then((res) => {
        setJobs(res.jobs)
        if (preselectedJobId && res.jobs.some((j) => j.id === preselectedJobId)) {
          setSelectedJobId(preselectedJobId)
          setFormData((prev) => ({ ...prev, recruiterJobId: preselectedJobId }))
        }
      })
      .catch(() => {
        /* silently ignore */
      })
      .finally(() => setJobsLoading(false))
  }, [])

  const selectedJob = jobs.find((j) => j.id === selectedJobId) ?? null
  const jobIsReady = selectedJob !== null && selectedJob.embeddingProcessedAt !== null
  const isSelectedJobProcessing = selectedJob?.embeddingStatus !== 'FAILED'
  const hasNoExtractedSkills =
    jobIsReady && (!selectedJob?.extractedSkills || selectedJob.extractedSkills.length === 0)
  const hasNoRelevantSkills =
    jobIsReady && !hasNoExtractedSkills && selectedJob?.relevantSkillsCount === 0
  const canSubmit =
    !!selectedJobId &&
    jobIsReady &&
    !isLoading &&
    !isLoadingPreview &&
    !isReprocessing &&
    !hasNoExtractedSkills &&
    !hasNoRelevantSkills

  const handleJobSelect = (jobId: string) => {
    setSelectedJobId(jobId)
    setFormData((prev) => ({ ...prev, recruiterJobId: jobId }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    await onSubmit(formData)
  }

  const addLocation = () => {
    if (locationInput.trim() && !formData.filters?.location?.includes(locationInput.trim())) {
      setFormData({
        ...formData,
        filters: {
          ...formData.filters,
          location: [...(formData.filters?.location || []), locationInput.trim()],
        },
      })
      setLocationInput('')
    }
  }

  const removeLocation = (location: string) => {
    setFormData({
      ...formData,
      filters: {
        ...formData.filters,
        location: formData.filters?.location?.filter((l) => l !== location) || [],
      },
    })
  }

  const addEmploymentStatus = (status: string) => {
    if (status && !formData.filters?.employmentStatus?.includes(status)) {
      setFormData({
        ...formData,
        filters: {
          ...formData.filters,
          employmentStatus: [...(formData.filters?.employmentStatus || []), status],
        },
      })
    }
  }

  const removeEmploymentStatus = (status: string) => {
    setFormData({
      ...formData,
      filters: {
        ...formData.filters,
        employmentStatus: formData.filters?.employmentStatus?.filter((s) => s !== status) || [],
      },
    })
  }

  const availableEmploymentStatuses = employmentStatusOptions.filter(
    (option) => !formData.filters?.employmentStatus?.includes(option.value)
  )

  // EMBEDDINGS_NOT_READY error state
  if (embeddingsError && onReprocess) {
    const errorJob = jobs.find((j) => j.id === embeddingsError.jobId)
    const isErrorJobProcessing = errorJob?.embeddingStatus !== 'FAILED'
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-5">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h4 className="text-sm font-semibold text-amber-900 mb-1">
                Job embeddings not ready
              </h4>
              <p className="text-sm text-amber-800 mb-3">
                The AI analysis for{' '}
                <span className="font-medium">{errorJob?.title ?? 'this job'}</span> hasn't been
                computed yet — likely because the AI service was temporarily unavailable when the
                job was created. Click below to run the analysis now, then try again.
              </p>
              <Button
                type="button"
                variant="primary"
                onClick={() => onReprocess(embeddingsError.jobId)}
                disabled={isReprocessing || isErrorJobProcessing}
                className="flex items-center gap-2"
              >
                {isReprocessing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Processing…
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    Reprocess job
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Job selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select a job <span className="text-red-500">*</span>
        </label>

        {jobsLoading ? (
          <div className="flex items-center gap-2 py-3 text-sm text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading jobs…
          </div>
        ) : jobs.length === 0 ? (
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-800">
            You don't have any jobs yet.{' '}
            <button
              type="button"
              onClick={() => navigate('/recruiter/job-offers')}
              className="font-medium underline hover:no-underline"
            >
              Create a job first
            </button>{' '}
            in the Jobs tab, then come back to run AI matching.
          </div>
        ) : (
          <>
            <select
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={selectedJobId}
              onChange={(e) => handleJobSelect(e.target.value)}
            >
              <option value="">Choose a job…</option>
              {jobs.map((job) => {
                const ready = job.embeddingProcessedAt !== null
                return (
                  <option key={job.id} value={job.id} disabled={!ready}>
                    {job.title}
                    {job.position ? ` — ${job.position}` : ''}
                    {!ready ? ' (not ready — reprocess needed)' : ''}
                  </option>
                )
              })}
            </select>

            {/* Selected job status indicator + extracted skills */}
            {selectedJob && (
              <div className="mt-2 space-y-2">
                {jobIsReady && !hasNoExtractedSkills && !hasNoRelevantSkills ? (
                  <p className="flex items-center gap-1.5 text-sm text-green-700">
                    <CheckCircle2 className="w-4 h-4" />
                    Ready for AI matching
                  </p>
                ) : jobIsReady && hasNoExtractedSkills ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900 mb-0.5">
                          No skills extracted
                        </p>
                        <p className="text-sm text-amber-800 mb-2">
                          The AI found no skills, responsibilities, or requirements in this job
                          description. Update the description with specific skills and
                          responsibilities so AI matching can work effectively.
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/recruiter/jobs/${selectedJob.id}`)}
                          className="flex items-center gap-1.5"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Edit job description
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : jobIsReady && hasNoRelevantSkills ? (
                  <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-amber-900 mb-0.5">
                          No recognised skills
                        </p>
                        <p className="text-sm text-amber-800 mb-2">
                          None of the skills extracted from this job description match skills in our
                          database. Update the description to include more widely-recognised skills
                          before running AI match.
                        </p>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => navigate(`/recruiter/jobs/${selectedJob.id}`)}
                          className="flex items-center gap-1.5"
                        >
                          <ExternalLink className="w-3 h-3" />
                          Edit job description
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : !jobIsReady ? (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3">
                    <Clock className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm text-amber-800">
                        This job's AI analysis hasn't been computed yet.
                      </p>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => onReprocess?.(selectedJob.id)}
                        disabled={isReprocessing || isSelectedJobProcessing}
                        className="mt-2 flex items-center gap-1.5"
                      >
                        {isReprocessing ? (
                          <>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Processing…
                          </>
                        ) : (
                          <>
                            <RefreshCw className="w-3 h-3" />
                            Reprocess
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ) : null}

                {/* Extracted skills chips — shown whenever the job has skills */}
                {jobIsReady &&
                  selectedJob.extractedSkills &&
                  selectedJob.extractedSkills.length > 0 && (
                    <div className="rounded-md border border-gray-200 bg-gray-50 p-3">
                      <p className="flex items-center gap-1 text-xs font-medium text-gray-500 mb-2">
                        <Sparkles className="w-3 h-3" />
                        Extracted skills ({selectedJob.extractedSkills.length})
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="px-2.5 py-1 rounded-full text-sm font-semibold bg-purple-600 text-white border border-purple-700">
                            {selectedJob.extractedSkills[0]}
                          </span>
                          <span className="text-xs text-purple-700 font-medium">Primary Skill</span>
                        </div>
                        {selectedJob.extractedSkills.length > 1 && (
                          <div className="flex flex-wrap gap-1.5">
                            {selectedJob.extractedSkills.slice(1).map((skill) => (
                              <span
                                key={skill}
                                className="px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200"
                              >
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Filters */}
      <div className="border-t pt-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters (Optional)</h3>

        {/* Location */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Locations</label>
          <div className="flex gap-2">
            <Input
              type="text"
              value={locationInput}
              onChange={(e) => setLocationInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  addLocation()
                }
              }}
              placeholder="e.g., New York, Remote"
            />
            <Button type="button" onClick={addLocation} variant="secondary">
              Add
            </Button>
          </div>
          {formData.filters?.location && formData.filters.location.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.filters.location.map((location) => (
                <span
                  key={location}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                >
                  {location}
                  <button
                    type="button"
                    onClick={() => removeLocation(location)}
                    className="hover:text-blue-900"
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Experience range */}
        <div className="mb-4">
          <RangeSlider
            label="Experience (years)"
            min={0}
            max={30}
            step={1}
            value={[formData.filters?.minExperience ?? 0, formData.filters?.maxExperience ?? 30]}
            onChange={([min, max]) => {
              setFormData({
                ...formData,
                filters: {
                  ...formData.filters,
                  minExperience: min === 0 ? undefined : min,
                  maxExperience: max === 30 ? undefined : max,
                },
              })
            }}
          />
        </div>

        {/* Employment status */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Employment Status</label>
          <select
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            onChange={(e) => {
              if (e.target.value) {
                addEmploymentStatus(e.target.value)
                e.target.value = ''
              }
            }}
            value=""
            disabled={availableEmploymentStatuses.length === 0}
          >
            <option value="">
              {availableEmploymentStatuses.length === 0
                ? 'All statuses selected'
                : 'Select employment status…'}
            </option>
            {availableEmploymentStatuses.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {formData.filters?.employmentStatus && formData.filters.employmentStatus.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.filters.employmentStatus.map((status) => {
                const option = employmentStatusOptions.find((opt) => opt.value === status)
                return (
                  <span
                    key={status}
                    className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm"
                  >
                    {option?.label || status}
                    <button
                      type="button"
                      onClick={() => removeEmploymentStatus(status)}
                      className="hover:text-green-900"
                    >
                      ×
                    </button>
                  </span>
                )
              })}
            </div>
          )}
          <p className="mt-1 text-xs text-gray-500">
            Select multiple statuses to filter candidates
          </p>
        </div>
      </div>

      {/* Submit buttons */}
      <div className="flex gap-3 pt-4 border-t">
        <Button
          type="button"
          onClick={() => navigate('/recruiter/ai-matching')}
          variant="secondary"
          disabled={isLoading || isLoadingPreview || isReprocessing}
          className="flex-1"
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={!canSubmit} className="flex-1">
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Finding Matches…
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4 mr-2" />
              Find AI Matches
            </>
          )}
        </Button>
      </div>
    </form>
  )
}
